import { useState, useEffect } from 'react';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import { toast } from 'react-toastify';
import AlertDialog from '../AlertDialog';
import { fetchAutoQuotationRecords, deleteAutoQuotationRecord } from '../../services/AutoQuotationService';

function formatDate(value) {
  if (!value) return '';
  return moment(value).format('MM/DD/YYYY');
}

export default function AutoQuotationHistory({ open, anchorEl, onClose, onSelect, onNew, onDelete, token, selectedRecordId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRecord, setMenuRecord] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedRecordIdState, setSelectedRecordIdState] = useState(selectedRecordId);

  useEffect(() => {
    setSelectedRecordIdState(selectedRecordId);
  }, [selectedRecordId]);

  useEffect(() => {
    if (!deleteConfirmOpen) {
      setRecordToDelete(null);
    }
  }, [deleteConfirmOpen]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);

    fetchAutoQuotationRecords({ token })
      .then(response => {
        if (!mounted) return;
        setRecords(response?.data?.auto_quotation_records ?? []);
      })
      .catch(() => {
        if (!mounted) return;
        setError(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const handleOpenMenu = (event, record) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRecord(record);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRecord(null);
  };

  const handleRequestDeleteRecord = () => {
    if (!menuRecord) return;
    setRecordToDelete(menuRecord);
    setDeleteConfirmOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDeleteRecord = async () => {
    if (!recordToDelete) {
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      await deleteAutoQuotationRecord({ recordId: recordToDelete.id, token });
      setRecords((prevRecords) => prevRecords.filter((record) => record.id !== recordToDelete.id));
      if (selectedRecordIdState === recordToDelete.id) {
        setSelectedRecordIdState(null);
      }
      onDelete?.(recordToDelete);
      toast.success('Quotation deleted.', { hideProgressBar: true });
    } catch (error) {
      console.error('Failed to delete auto quotation record:', error);
      toast.error('Unable to delete quotation.', { hideProgressBar: true });
    } finally {
      setRecordToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      {loading ? (
        <Box sx={{ width: 400, display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} sx={{ color: '#673ab7' }} />
        </Box>
      ) : error ? (
        <Box sx={{ width: 400, p: 2 }}>
          <Typography color="error">Unable to load quotation history.</Typography>
        </Box>
      ) : (
        <List dense disablePadding sx={{ width: 400 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                onNew?.();
                onClose?.();
              }}
            >
              <ListItemText primary="New Quotation" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
          <Divider />
          {records.length === 0 ? (
            <ListItem>
              <ListItemText primary="No quotation history found." />
            </ListItem>
          ) : (
            records.map(record => (
              <ListItem
                key={record.id}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={(event) => handleOpenMenu(event, record)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemButton
                  sx={{
                    backgroundColor:
                      selectedRecordIdState === record.id ? '#ede7f6' : 'inherit',
                  }}
                  onClick={() => {
                    setSelectedRecordIdState(record.id);
                    onSelect?.(record);
                    onClose?.();
                  }}
                >
                  <ListItemText
                    primary={`${record.provided_for}'s Quotation (${record.quote_no}) - ${formatDate(record.created_at)}`}
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleCloseMenu}
          >
            <MenuItem dense onClick={handleRequestDeleteRecord}>
              Delete
            </MenuItem>
          </Menu>
        </List>
      )}
      <AlertDialog
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        title="Delete Quotation"
        content={recordToDelete ? `Are you sure you want to delete the quotation for <strong>${recordToDelete.provided_for || 'this client'}</strong>?` : 'Are you sure you want to delete this quotation?'}
        positiveButtonLabel="Delete"
        negativeButtonLabel="Cancel"
        callback={handleConfirmDeleteRecord}
      />
    </Popover>
  );
}
